// src/api/googleCalendar.js
// Google Calendar API Service for fetching calendar events

import { getStoredTokens, refreshAccessToken, getGoogleAuthUrl } from './googleDrive';

const CALENDAR_API_BASE = 'https://www.googleapis.com/calendar/v3';

/**
 * Get valid access token (refresh if needed)
 */
const getValidAccessToken = async () => {
  let tokens = getStoredTokens();

  if (!tokens) {
    throw new Error('NOT_AUTHENTICATED:' + getGoogleAuthUrl());
  }

  if (tokens.needsRefresh) {
    tokens = await refreshAccessToken(tokens.refresh_token);
  }

  return tokens.access_token;
};

/**
 * Fetch list of calendars the user has access to
 */
export const getCalendarList = async () => {
  const accessToken = await getValidAccessToken();

  const response = await fetch(`${CALENDAR_API_BASE}/users/me/calendarList`, {
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to fetch calendars');
  }

  const data = await response.json();
  return data.items || [];
};

/**
 * Fetch events from a calendar for a specific date
 * @param {string} calendarId - Calendar ID (use 'primary' for main calendar)
 * @param {string} date - Date in YYYY-MM-DD format
 */
export const getEventsForDate = async (calendarId = 'primary', date) => {
  const accessToken = await getValidAccessToken();

  // Parse date parts manually to avoid timezone issues
  const [year, month, day] = date.split('-').map(Number);

  // Create start and end of day in local timezone
  const startOfDay = new Date(year, month - 1, day, 0, 0, 0, 0);
  const endOfDay = new Date(year, month - 1, day, 23, 59, 59, 999);

  const params = new URLSearchParams({
    timeMin: startOfDay.toISOString(),
    timeMax: endOfDay.toISOString(),
    singleEvents: 'true',
    orderBy: 'startTime',
    maxResults: '100',
  });

  const response = await fetch(
    `${CALENDAR_API_BASE}/calendars/${encodeURIComponent(calendarId)}/events?${params}`,
    {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to fetch events');
  }

  const data = await response.json();
  return data.items || [];
};

/**
 * Parse event data into a more usable format
 */
export const parseEvent = (event) => {
  // Handle all-day events vs timed events
  const startDateTime = event.start?.dateTime || event.start?.date;
  const endDateTime = event.end?.dateTime || event.end?.date;

  const isAllDay = !event.start?.dateTime;

  let startTime = null;
  let endTime = null;

  if (!isAllDay) {
    const start = new Date(startDateTime);
    const end = new Date(endDateTime);
    startTime = start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    endTime = end.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  }

  return {
    id: event.id,
    title: event.summary || 'Sans titre',
    description: event.description || '',
    location: event.location || '',
    startDateTime,
    endDateTime,
    startTime,
    endTime,
    isAllDay,
    colorId: event.colorId,
    htmlLink: event.htmlLink,
    attendees: event.attendees || [],
    organizer: event.organizer,
    status: event.status,
    // Keep original event for reference
    _original: event,
  };
};

/**
 * Get events for a date with parsed data
 */
export const getFormattedEventsForDate = async (calendarId = 'primary', date) => {
  const events = await getEventsForDate(calendarId, date);
  return events.map(parseEvent);
};

/**
 * Generate summary statistics for a day's events
 */
export const getDaySummary = (events) => {
  const summary = {
    totalEvents: events.length,
    allDayEvents: 0,
    timedEvents: 0,
    earliestTime: null,
    latestTime: null,
    eventTypes: {},
  };

  events.forEach(event => {
    if (event.isAllDay) {
      summary.allDayEvents++;
    } else {
      summary.timedEvents++;

      // Track earliest and latest times
      if (!summary.earliestTime || event.startTime < summary.earliestTime) {
        summary.earliestTime = event.startTime;
      }
      if (!summary.latestTime || event.endTime > summary.latestTime) {
        summary.latestTime = event.endTime;
      }
    }

    // Try to categorize events based on title keywords
    const title = event.title.toLowerCase();
    let type = 'Autre';

    if (title.includes('charter') || title.includes('yacht') || title.includes('bateau')) {
      type = 'Charter';
    } else if (title.includes('lunch') || title.includes('dejeuner') || title.includes('déjeuner')) {
      type = 'Lunch';
    } else if (title.includes('transfert') || title.includes('transfer')) {
      type = 'Transfert';
    } else if (title.includes('seabob')) {
      type = 'Seabob';
    } else if (title.includes('jetski') || title.includes('jet-ski') || title.includes('jet ski')) {
      type = 'Jet-ski';
    }

    summary.eventTypes[type] = (summary.eventTypes[type] || 0) + 1;
  });

  return summary;
};

export default {
  getCalendarList,
  getEventsForDate,
  getFormattedEventsForDate,
  getDaySummary,
  parseEvent,
};
